import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions } from 'react-native';
import { IActivity, KeyStage, GroupingSortingConfig, SortItem, SortCategory } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { StageCard, JuicyButton } from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { RotateCcw, Sparkles } from 'lucide-react-native';

interface GroupingSortingActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const GroupingSortingActivity: React.FC<GroupingSortingActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as GroupingSortingConfig;
    const categories: SortCategory[] = config.categories || [
        { id: 'cat_a', label: 'Group A', color: '#0D9488' },
        { id: 'cat_b', label: 'Group B', color: '#6366F1' },
    ];
    const items: SortItem[] = config.items || [];

    const [placements, setPlacements] = useState<Record<string, string>>({});
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState(false);

    const { width, height } = useWindowDimensions();
    const isCompact = width < 600 || height < 750;
    const isNarrow = width < 480;

    const unsortedItems = useMemo(() => {
        return items.filter((item) => !placements[item.id]);
    }, [items, placements]);

    const isAllPlaced = items.length > 0 && unsortedItems.length === 0;

    const handleSelectItem = (item: SortItem) => {
        setSelectedItemId(item.id === selectedItemId ? null : item.id);
    };

    const handlePlaceInCat = (catId: string) => {
        if (!selectedItemId) return;
        setPlacements((prev) => ({ ...prev, [selectedItemId]: catId }));
        setSelectedItemId(null);
    };

    const handleQuickAssign = (itemId: string, catId: string) => {
        setPlacements((prev) => ({ ...prev, [itemId]: catId }));
        if (selectedItemId === itemId) setSelectedItemId(null);
    };

    const handleRemovePlacement = (itemId: string) => {
        setPlacements((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
        setShowValidation(false);
    };

    const handleReset = () => {
        setPlacements({});
        setSelectedItemId(null);
        setShowValidation(false);
    };

    const handleSubmit = () => {
        setShowValidation(true);

        const correctCount = items.filter((item) => placements[item.id] === item.category).length;
        const allCorrect = items.length > 0 && correctCount === items.length;

        useStrokeStore.getState().setStructuredData({
            placements,
            correctCount,
            totalItems: items.length,
            isCorrect: allCorrect,
            answer: allCorrect ? 'Correct' : 'Partial',
            inputModeUsed: 'drag_sort',
        });

        if (allCorrect) {
            onComplete();
        }
    };

    const promptText = config.audioPromptText || config.prompt || activity.instructions || 'Sort the items into the correct groups!';

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={[
                styles.scrollContent,
                isCompact && styles.scrollContentCompact,
            ]}
            showsVerticalScrollIndicator={true}
            bounces={false}
        >
            <StageCard style={[styles.stageCard, isCompact && styles.stageCardCompact]}>
                {/* Header with Prompt and Audio Button */}
                <View style={[styles.promptHeader, isCompact && styles.promptHeaderCompact]}>
                    <View style={styles.promptTextWrap}>
                        <Text style={[styles.titleText, isCompact && styles.titleTextCompact]} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                            {activity.title || 'Sorting & Grouping'}
                        </Text>
                        <Text style={[styles.promptSubText, isCompact && styles.promptSubTextCompact]} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                            {promptText}
                        </Text>
                    </View>
                    <AudioPromptButton
                        text={promptText}
                        compact={isNarrow}
                    />
                </View>

                {/* Unsorted Items Tray */}
                <View style={[styles.trayArea, isCompact && styles.trayAreaCompact]}>
                    <View style={styles.trayHeader}>
                        <Text style={[styles.trayTitle, isCompact && styles.trayTitleCompact]}>Items to Sort:</Text>
                        <Text style={styles.trayCountBadge}>
                            {unsortedItems.length} left
                        </Text>
                    </View>

                    {isCompact ? (
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.itemsScrollContent}
                            style={styles.itemsScrollView}
                        >
                            {unsortedItems.map((item) => {
                                const isSelected = item.id === selectedItemId;
                                return (
                                    <View key={item.id} style={styles.cardWithButtonsCompact}>
                                        <TouchableOpacity
                                            style={[
                                                styles.itemCard,
                                                styles.itemCardCompact,
                                                isSelected && styles.itemCardSelected,
                                            ]}
                                            onPress={() => handleSelectItem(item)}
                                            activeOpacity={0.7}
                                            accessibilityRole="button"
                                            accessibilityLabel={`Select ${item.label}`}
                                            testID={`sort-item-${item.id}`}
                                        >
                                            {item.icon && <Text style={styles.itemIconCompact}>{item.icon}</Text>}
                                            <Text style={styles.itemLabelCompact} numberOfLines={1}>{item.label}</Text>
                                        </TouchableOpacity>

                                        {/* Direct Quick Assign Buttons */}
                                        <View style={styles.quickAssignRowCompact}>
                                            {categories.map((cat) => (
                                                <TouchableOpacity
                                                    key={cat.id}
                                                    style={[
                                                        styles.quickPillCompact,
                                                        { backgroundColor: cat.color || '#0D9488' },
                                                    ]}
                                                    onPress={() => handleQuickAssign(item.id, cat.id)}
                                                    activeOpacity={0.7}
                                                    testID={`quick-assign-${item.id}-${cat.id}`}
                                                >
                                                    <Text style={styles.quickPillTextCompact} numberOfLines={1}>{cat.label}</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>
                                );
                            })}

                            {unsortedItems.length === 0 && (
                                <View style={styles.allPlacedWrap}>
                                    <Sparkles size={20} color="#15803D" />
                                    <Text style={[styles.allPlacedText, styles.allPlacedTextCompact]}>All items placed in groups!</Text>
                                </View>
                            )}
                        </ScrollView>
                    ) : (
                        <View style={styles.itemsRow}>
                            {unsortedItems.map((item) => {
                                const isSelected = item.id === selectedItemId;
                                return (
                                    <View key={item.id} style={styles.cardWithButtons}>
                                        <TouchableOpacity
                                            style={[styles.itemCard, isSelected && styles.itemCardSelected]}
                                            onPress={() => handleSelectItem(item)}
                                            activeOpacity={0.7}
                                            accessibilityRole="button"
                                            accessibilityLabel={`Select ${item.label}`}
                                            testID={`sort-item-${item.id}`}
                                        >
                                            {item.icon && <Text style={styles.itemIcon}>{item.icon}</Text>}
                                            <Text style={styles.itemLabel}>{item.label}</Text>
                                        </TouchableOpacity>

                                        {/* Direct Quick Assign Buttons */}
                                        <View style={styles.quickAssignRow}>
                                            {categories.map((cat) => (
                                                <TouchableOpacity
                                                    key={cat.id}
                                                    style={[
                                                        styles.quickPill,
                                                        { backgroundColor: cat.color || '#0D9488' },
                                                    ]}
                                                    onPress={() => handleQuickAssign(item.id, cat.id)}
                                                    activeOpacity={0.7}
                                                    testID={`quick-assign-${item.id}-${cat.id}`}
                                                >
                                                    <Text style={styles.quickPillText}>{cat.label}</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>
                                );
                            })}

                            {unsortedItems.length === 0 && (
                                <View style={styles.allPlacedWrap}>
                                    <Sparkles size={24} color="#15803D" />
                                    <Text style={styles.allPlacedText}>All items placed in groups!</Text>
                                </View>
                            )}
                        </View>
                    )}
                </View>

                {/* Category Buckets Container */}
                <View style={[styles.categoriesArena, isCompact && styles.categoriesArenaCompact]}>
                    {categories.map((cat) => {
                        const catItems = items.filter((item) => placements[item.id] === cat.id);
                        const canDrop = !!selectedItemId;

                        return (
                            <TouchableOpacity
                                key={cat.id}
                                style={[
                                    styles.categoryBox,
                                    isCompact && styles.categoryBoxCompact,
                                    canDrop && styles.categoryBoxDroppable,
                                    { borderColor: cat.color || '#CBD5E1' },
                                ]}
                                onPress={() => handlePlaceInCat(cat.id)}
                                activeOpacity={canDrop ? 0.7 : 1}
                                accessibilityRole="button"
                                accessibilityLabel={`Group ${cat.label}`}
                                testID={`cat-bucket-${cat.id}`}
                            >
                                <View
                                    style={[
                                        styles.catHeader,
                                        isCompact && styles.catHeaderCompact,
                                        { backgroundColor: cat.color || '#0D9488' },
                                    ]}
                                >
                                    <Text style={[styles.catTitle, isCompact && styles.catTitleCompact]} numberOfLines={1}>{cat.label}</Text>
                                    <Text style={[styles.catCountBadge, isCompact && styles.catCountBadgeCompact]}>{catItems.length}</Text>
                                </View>

                                <View style={[styles.catContents, isCompact && styles.catContentsCompact]}>
                                    {catItems.map((item) => {
                                        const isMisplaced = showValidation && item.category !== cat.id;
                                        return (
                                            <TouchableOpacity
                                                key={item.id}
                                                style={[
                                                    styles.placedItemPill,
                                                    isCompact && styles.placedItemPillCompact,
                                                    isMisplaced && styles.placedItemMisplaced,
                                                ]}
                                                onPress={() => handleRemovePlacement(item.id)}
                                                activeOpacity={0.7}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Remove ${item.label}`}
                                                testID={`placed-item-${item.id}`}
                                            >
                                                {item.icon && (
                                                    <Text style={[styles.placedItemIcon, isCompact && styles.placedItemIconCompact]}>{item.icon}</Text>
                                                )}
                                                <Text style={[styles.placedItemText, isCompact && styles.placedItemTextCompact]} numberOfLines={1}>{item.label}</Text>
                                                <Text style={styles.removeX}>✕</Text>
                                            </TouchableOpacity>
                                        );
                                    })}

                                    {catItems.length === 0 && (
                                        <Text style={[styles.catEmptyText, isCompact && styles.catEmptyTextCompact]}>
                                            {canDrop ? 'Tap here to put item in this group!' : 'Empty group'}
                                        </Text>
                                    )}
                                </View>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {/* Footer Controls: Reset & Hero "GO!" Button */}
                <View style={[styles.footerRow, isCompact && styles.footerRowCompact]}>
                    <TouchableOpacity
                        style={[styles.resetButton, isCompact && styles.resetButtonCompact]}
                        onPress={handleReset}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel="Reset sorting"
                        testID="sort-reset-button"
                    >
                        <RotateCcw size={isCompact ? 16 : 20} color="#64748B" />
                        <Text style={[styles.resetButtonText, isCompact && styles.resetButtonTextCompact]}>Reset</Text>
                    </TouchableOpacity>

                    <JuicyButton
                        label={isAllPlaced ? 'CHECK & GO! 🚀' : 'Sort all items'}
                        variant="primary"
                        size={isCompact ? 'medium' : 'large'}
                        disabled={!isAllPlaced}
                        onPress={handleSubmit}
                        style={styles.heroGoButton}
                        testID="grouping-sort-go-btn"
                    />
                </View>
            </StageCard>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    scrollContent: {
        paddingHorizontal: 16,
        paddingBottom: 40,
        alignItems: 'center',
    },
    scrollContentCompact: {
        paddingHorizontal: 8,
        paddingBottom: 24,
    },
    stageCard: {
        width: '100%',
        maxWidth: 780,
        padding: 24,
        alignItems: 'stretch',
    },
    stageCardCompact: {
        padding: 12,
        borderRadius: 24,
    },
    promptHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#F5F3FF',
        padding: 16,
        borderRadius: 20,
        borderWidth: 1.5,
        borderColor: '#DDD6FE',
        marginBottom: 16,
        gap: 12,
    },
    promptHeaderCompact: {
        padding: 10,
        borderRadius: 16,
        marginBottom: 10,
        gap: 8,
    },
    promptTextWrap: {
        flex: 1,
    },
    titleText: {
        fontSize: 22,
        fontWeight: '900',
        color: '#6D28D9',
        marginBottom: 4,
    },
    titleTextCompact: {
        fontSize: 18,
        marginBottom: 2,
    },
    promptSubText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#4C1D95',
    },
    promptSubTextCompact: {
        fontSize: 14,
    },
    trayArea: {
        backgroundColor: '#F8FAFC',
        padding: 16,
        borderRadius: 20,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        marginBottom: 20,
    },
    trayAreaCompact: {
        padding: 10,
        borderRadius: 16,
        marginBottom: 12,
    },
    trayHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    trayTitle: {
        fontSize: 15,
        fontWeight: '800',
        color: '#475569',
    },
    trayTitleCompact: {
        fontSize: 13,
    },
    trayCountBadge: {
        fontSize: 13,
        fontWeight: '800',
        color: '#64748B',
        backgroundColor: '#E2E8F0',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 12,
    },
    itemsScrollView: {
        width: '100%',
    },
    itemsScrollContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 4,
    },
    itemsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 80,
    },
    cardWithButtons: {
        alignItems: 'center',
        gap: 4,
    },
    cardWithButtonsCompact: {
        alignItems: 'center',
        gap: 4,
        minWidth: 85,
    },
    itemCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        borderColor: '#CBD5E1',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 3,
        elevation: 2,
    },
    itemCardCompact: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 12,
        gap: 6,
    },
    itemCardSelected: {
        borderColor: '#6366F1',
        backgroundColor: '#EEF2FF',
        transform: [{ scale: 1.05 }],
    },
    itemIcon: {
        fontSize: 22,
    },
    itemIconCompact: {
        fontSize: 18,
    },
    itemLabel: {
        fontSize: 16,
        fontWeight: '800',
        color: '#1E293B',
    },
    itemLabelCompact: {
        fontSize: 13,
        fontWeight: '800',
        color: '#1E293B',
    },
    quickAssignRow: {
        flexDirection: 'row',
        gap: 4,
    },
    quickAssignRowCompact: {
        flexDirection: 'row',
        gap: 3,
    },
    quickPill: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 8,
    },
    quickPillCompact: {
        paddingHorizontal: 6,
        paddingVertical: 3,
        borderRadius: 6,
        maxWidth: 75,
    },
    quickPillText: {
        fontSize: 10,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    quickPillTextCompact: {
        fontSize: 9,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    allPlacedWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 12,
    },
    allPlacedText: {
        fontSize: 16,
        fontWeight: '800',
        color: '#15803D',
    },
    allPlacedTextCompact: {
        fontSize: 13,
    },
    categoriesArena: {
        flexDirection: 'row',
        gap: 16,
        marginBottom: 20,
    },
    categoriesArenaCompact: {
        gap: 8,
        marginBottom: 12,
    },
    categoryBox: {
        flex: 1,
        minHeight: 190,
        borderRadius: 20,
        backgroundColor: '#FFFFFF',
        borderWidth: 2.5,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 4,
        elevation: 2,
    },
    categoryBoxCompact: {
        minHeight: 110,
        borderRadius: 16,
    },
    categoryBoxDroppable: {
        borderStyle: 'dashed',
        backgroundColor: '#FAF5FF',
    },
    catHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 14,
        paddingVertical: 8,
    },
    catHeaderCompact: {
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    catTitle: {
        fontSize: 16,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    catTitleCompact: {
        fontSize: 13,
    },
    catCountBadge: {
        fontSize: 12,
        fontWeight: '900',
        color: '#FFFFFF',
        backgroundColor: 'rgba(0,0,0,0.2)',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
    },
    catCountBadgeCompact: {
        fontSize: 11,
        paddingHorizontal: 6,
        paddingVertical: 1,
    },
    catContents: {
        padding: 12,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: 130,
    },
    catContentsCompact: {
        padding: 8,
        gap: 6,
        minHeight: 65,
    },
    placedItemPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 12,
        backgroundColor: '#F1F5F9',
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
    },
    placedItemPillCompact: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 10,
        gap: 4,
    },
    placedItemMisplaced: {
        backgroundColor: '#FEE2E2',
        borderColor: '#EF4444',
    },
    placedItemIcon: {
        fontSize: 16,
    },
    placedItemIconCompact: {
        fontSize: 14,
    },
    placedItemText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#1E293B',
    },
    placedItemTextCompact: {
        fontSize: 12,
    },
    removeX: {
        fontSize: 12,
        fontWeight: '900',
        color: '#94A3B8',
        marginLeft: 2,
    },
    catEmptyText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#94A3B8',
        fontStyle: 'italic',
        width: '100%',
        textAlign: 'center',
        paddingTop: 30,
    },
    catEmptyTextCompact: {
        paddingTop: 16,
        fontSize: 12,
    },
    footerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
    },
    footerRowCompact: {
        gap: 10,
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: 60,
        paddingHorizontal: 18,
        borderRadius: 20,
        backgroundColor: '#F1F5F9',
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
    },
    resetButtonCompact: {
        height: 48,
        paddingHorizontal: 14,
        borderRadius: 16,
    },
    resetButtonText: {
        fontSize: 16,
        fontWeight: '800',
        color: '#64748B',
    },
    resetButtonTextCompact: {
        fontSize: 14,
    },
    heroGoButton: {
        flex: 1,
    },
});
